import type { ReactNode } from 'react';
import { useEffect } from 'react';

import { COMPONENTLOAD_FAILURE } from '@shared/constants/newrelic-events/event-names';
import NotFound from '@shared/data-access-backend/global-components/not-found';
import { getGcsProps } from '@shared/data-access-backend/global-components-props';
import { googleMapsApiKey as getGoogleMapsApiKey } from '@shared/data-access-backend/keys';
import { setupPageTrackingAttrs } from '@shared/data-access-backend/setup-page-tracking';
import BackendContext from '@shared/data-access-frontend/contexts/backend-context';
import UBFFContext from '@shared/data-access-frontend/contexts/ubff-context';
import GlobalNotFound from '@shared/ui/components/NotFound';
import PageSkeleton from '@shared/ui/components/PageSkeleton';
import GoogleMapsContext from '@shared/ui/contexts/google-maps-context';
import { I18nContext } from '@shared/ui/contexts/i18n';
import { MiniEventTrackerContext } from '@shared/ui/contexts/mini-events';
import serverConfig from '@shared/utils-backend/config';
import { featureConfigs } from '@shared/utils-backend/feature-configs';
import ConfigurationContext from '@shared/utils-frontend/contexts/ConfigurationContext';
import LoggingContext from '@shared/utils-frontend/contexts/LoggingContext';
import UserContext from '@shared/utils-frontend/contexts/UserContext';
import logger, { initLogging } from '@shared/utils-frontend/logger';
import { getHoustonUrl } from '@shared/utils-frontend/url/houston-url';
import { getPageName } from '@shared/utils-universal/page-name';
import { getRumSetupHtml } from '@skyscanner-internal/mshell-node-rum';
import {
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from 'react-router';
import { requestContextParameters } from 'request-context-skyscanner';

import './components/backpack-foundation';
import { DynamicLinks } from './components/DynamicLinks';
import { DynamicScripts } from './components/DynamicScripts';
import { useContextValues } from './providers/useContextValues';
import { useDistilledExperimentTracking } from './providers/useDistilledExperimentTracking';
import { useErrorHandling } from './providers/useErrorHandling';
import { useHashScrollRestoration } from './providers/useHashScrollRestoration';
import { useIsProductionEnv } from './providers/useIsProductionEnv';
import { useLoadGtmClient } from './providers/useLoadGtmClient';
import { usePageLoadTracking } from './providers/usePageLoadTracking';
import { useSearchEntity } from './providers/useSearchEntity';
import { useStoreCashBackUserId } from './providers/useStoreCashBackUserId';

import 'custom-event-polyfill';

import type { ExtractLoaderData } from './types';
import type { LoaderFunctionArgs, AppLoadContext } from 'react-router';

export const loader = async ({
  context: {
    config,
    context,
    correlationId,
    distilledExperiment,
    gateway,
    i18nService,
    logger: serverLogger,
    params,
    perimeterXScriptAttrs,
    polyfillScriptAttrs,
    userContext,
  },
}: LoaderFunctionArgs<AppLoadContext>) => {
  const [notFound, gcs] = await Promise.all([
    // TODO: the ticket https://skyscanner.atlassian.net/browse/BD-10111 to remove this
    NotFound.getProps({ context }),
    getGcsProps({ config, context, gateway, logger: serverLogger }),
  ]);
  const culture = context[requestContextParameters.CULTURE];
  const {
    clientEnv,
    services: {
      backend: {
        baseUrl: { webapp: backendUrl },
      },
    },
  } = serverConfig;
  const isRightToLeft = context[requestContextParameters.IS_RIGHT_TO_LEFT];
  const canonicalLink =
    context[requestContextParameters.PAGE_CONTEXT_DATA]?.canonical_url;
  const { pageType } = context[requestContextParameters.TRACKING_CONTEXT];
  const pageName = getPageName(
    pageType,
    userContext.enableSEOVariantHotelsDetailsPage,
  );
  const rumSetupHtml = getRumSetupHtml(
    {},
    pageName,
    {
      pageType,
      market: culture.market,
    },
    { includeScriptTag: false },
  );
  const trackingSetupAttrs = setupPageTrackingAttrs({
    context,
    isSeoTraffic: userContext.enableSEOVariantHotelsDetailsPage,
  });

  return data({
    clientEnv,
    backendUrl,
    distilledExperiment,
    i18n: {
      culture,
      cldr: i18nService.cldr(),
      translations: i18nService.translations(),
    },
    configs: featureConfigs(config),
    correlationId,
    userContext,
    gcs,
    notFound,
    googleMapsApiKey: getGoogleMapsApiKey(),
    eventSourcePolyfillEnabled: params.eventSourcePolyfill !== 'false',
    dir: isRightToLeft ? 'rtl' : 'ltr',
    canonicalLink,
    rumSetupHtml,
    trackingSetupAttrs,
    perimeterXScriptAttrs,
    polyfillScriptAttrs,
  });
};

export { shouldRevalidateProvider as shouldRevalidate } from './providers/shouldRevalidateProvider';

export function Layout({ children }: { readonly children: ReactNode }) {
  const {
    canonicalLink,
    dir,
    i18n: {
      culture: { locale },
    },
    perimeterXScriptAttrs,
    polyfillScriptAttrs,
    rumSetupHtml,
    trackingSetupAttrs,
  } = useLoaderData<ExtractLoaderData<typeof loader>>()!;
  return (
    <html lang={locale} dir={dir}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#05203c" />
        <meta name="msapplication-TileColor" content="#21c4d9" />
        <meta name="y_key" content="3ccd97fca3bc5b05" />
        <Meta />
        <link rel="manifest" href="/android-chrome-manifest.json" />
        <link
          rel="preconnect"
          href="https://js.skyscnr.com"
          crossOrigin="anonymous"
        />
        <link rel="preconnect" href="https://slipstream.skyscanner.net" />
        <link rel="dns-prefetch" href="https://js.skyscnr.com" />
        <link rel="dns-prefetch" href="https://secure.skyscanner.net" />
        <link rel="dns-prefetch" href="//www.googletagservices.com" />
        <link rel="dns-prefetch" href="//analytics.skyscanner.net" />
        {canonicalLink && <link rel="canonical" href={canonicalLink} />}
        <Links />
        <DynamicLinks />
        <DynamicScripts />
        {polyfillScriptAttrs?.src && <script {...polyfillScriptAttrs} />}
        <script {...perimeterXScriptAttrs} />
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: rumSetupHtml,
          }}
        />
      </head>
      <body>
        {/* #pagewrap used for accessibility by Backpack components, do not change */}
        <div id="pagewrap">
          {children}
          <script {...trackingSetupAttrs} />
          <Scripts />
        </div>
      </body>
    </html>
  );
}

export default function App() {
  const {
    backendUrl,
    clientEnv,
    configs,
    correlationId,
    distilledExperiment,
    eventSourcePolyfillEnabled,
    gcs,
    googleMapsApiKey,
    i18n,
    userContext,
  } = useLoaderData<ExtractLoaderData<typeof loader>>()!;

  const { entityId, nationId } = useSearchEntity();

  const {
    backendGateway,
    googleMaps,
    i18nService,
    logging,
    miniEventTracker,
    uBFFGateway,
  } = useContextValues({
    backendUrl,
    userContext,
    i18n,
    clientEnv,
    googleMapsApiKey,
    eventSourcePolyfillEnabled,
    correlationId,
  });
  const isProductionEnv = useIsProductionEnv();

  initLogging(logging);
  useErrorHandling();
  useLoadGtmClient({
    culture: i18n.culture,
    formatDate: i18nService.formatDate,
    userContext,
    enabled: configs.enableGTMIntegration,
  });
  useStoreCashBackUserId();
  useHashScrollRestoration();
  usePageLoadTracking({
    userContext,
    miniEventTracker,
  });
  useDistilledExperimentTracking({
    distilledExperiment,
    pageType: userContext.pageType,
  });

  return (
    <LoggingContext.Provider value={logger}>
      <MiniEventTrackerContext.Provider value={miniEventTracker}>
        <UBFFContext.Provider value={uBFFGateway}>
          <BackendContext.Provider value={backendGateway}>
            <GoogleMapsContext.Provider value={googleMaps}>
              <I18nContext.Provider value={i18nService}>
                <ConfigurationContext.Provider value={configs}>
                  <UserContext.Provider value={userContext}>
                    <PageSkeleton
                      gcs={gcs}
                      isProductionEnv={isProductionEnv}
                      entityId={entityId}
                      nationId={nationId}
                    >
                      <Outlet />
                    </PageSkeleton>
                  </UserContext.Provider>
                </ConfigurationContext.Provider>
              </I18nContext.Provider>
            </GoogleMapsContext.Provider>
          </BackendContext.Provider>
        </UBFFContext.Provider>
      </MiniEventTrackerContext.Provider>
    </LoggingContext.Provider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  const loaderData = useLoaderData<ExtractLoaderData<typeof loader>>();
  const is4xx =
    isRouteErrorResponse(error) && [400, 403, 404].includes(error.status);

  useEffect(() => {
    if (is4xx && loaderData?.notFound) return;

    const houstonUrl = getHoustonUrl({
      environment: loaderData.clientEnv,
      pageType: loaderData.userContext.pageType,
    });

    logger.event(COMPONENTLOAD_FAILURE);
    logger.error('Caught error in ErrorBoundary', {}, error);

    if (houstonUrl) {
      window.location.replace(houstonUrl);
    }
  }, [is4xx, loaderData?.notFound, loaderData, error]);

  if (is4xx && loaderData?.notFound) {
    return <GlobalNotFound {...loaderData.notFound} isProductionEnv />;
  }

  // At this point, we know we're in a non-production environment.
  //
  // In production:
  // - Server-side errors trigger a redirect to Houston at the web platform level.
  // - Client-side errors are handled via window.location.replace() in useEffect.
  //
  // Therefore, the only case where this fallback UI is ever shown is in development/e2e/staging environments.
  // We can safely render the raw error for debugging purposes here.

  // Important:
  // - Do NOT log server-side errors here.
  // - Instead, any server-side error logging must be handled in the entry.server.tsx or the loader/action functions for special cases.
  return (
    <pre>
      {(isRouteErrorResponse(error) &&
        `Route error ${error.status} ${error.statusText}\n${String(
          error.data ?? '',
        )}`) ||
        (error instanceof Error && `${error.message}\n${error.stack ?? ''}`) ||
        String(error)}
    </pre>
  );
}
